import { useFieldContext } from '~/util/hooks/useFormContext';
import {
    FieldLabelWithError,
    FieldWithErrorProps,
} from './FieldLabelWithError';
import { Role } from '~/util/types/users';
import { useState } from 'react';
import { useDisableFormContext } from '../DisableContext';
import { RoleTags, TagsBase } from '~/partials/Tags';
import { RolesSelector } from '~/components/ItemSelector';
import { InteractableDataWrapper } from '~/partials/InteractableDataWrapper';
import { cn } from '~/util';

export function RolesField({
    options = {},
    forceDisabled,
    ...labelProps
}: {
    forceDisabled?: boolean;
    options?: Partial<
        Omit<TagsBase<Role>, 'items' | 'editable' | 'onEdit' | 'item'>
    >;
} & FieldWithErrorProps) {
    const field = useFieldContext<Role[] | null>();
    const [rolesSelectorOpen, setRolesSelectorOpen] = useState(false);
    const disabled = useDisableFormContext() || forceDisabled;
    return (
        <FieldLabelWithError<Role[] | null> {...labelProps}>
            <RoleTags
                items={field.state.value ?? []}
                editable={!disabled}
                onEdit={() => setRolesSelectorOpen(true)}
                {...options}
            />
            <RolesSelector
                open={rolesSelectorOpen}
                onOpenChange={setRolesSelectorOpen}
                initial={field.state.value ?? []}
                onSave={(items) => {
                    field.handleChange(
                        (items instanceof Array ? items : [items]) ?? null,
                    );
                    setRolesSelectorOpen(false);
                }}
            />
        </FieldLabelWithError>
    );
}

export function RoleField({
    classname,
    forceDisabled,
    ...labelProps
}: {
    forceDisabled?: boolean;
    classname?: string;
} & FieldWithErrorProps) {
    const field = useFieldContext<Role | null>();
    const [roleSelectorOpen, setRoleSelectorOpen] = useState(false);
    const disabled = useDisableFormContext() || forceDisabled;
    return (
        <FieldLabelWithError<Role | null> {...labelProps}>
            <InteractableDataWrapper
                id={field.name}
                open={roleSelectorOpen}
                required={labelProps.required}
                disabled={disabled}
                className={cn(
                    field.state.value
                        ? 'text-secondary-foreground'
                        : 'text-secondary-light',
                    classname,
                    disabled && 'opacity-50',
                )}
                onClick={() => {
                    if (!disabled) setRoleSelectorOpen(true);
                }}
            >
                {field.state.value?.role_desc
                    ? field.state.value?.role_desc
                    : 'None'}
            </InteractableDataWrapper>
            <RolesSelector
                open={roleSelectorOpen}
                onOpenChange={setRoleSelectorOpen}
                initial={field.state.value ?? undefined}
                single
                onSave={(item) => {
                    field.handleChange(
                        (item instanceof Array ? item[0] : item) ?? null,
                    );
                    setRoleSelectorOpen(false);
                }}
            />
        </FieldLabelWithError>
    );
}
